import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Shirt, Save, Trash2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Small icon map (subset, matches PetEvolution ACCESSORY_MAP)
const ITEM_EMOJI = {
  hat_crown: '👑', hat_flower: '🌸', hat_wizard: '🎩', hat_party: '🎉',
  hat_beanie: '🧢', hat_sunhat: '👒', hat_snow: '🎩',
  hat_pumpkin: '🎃', hat_witch: '🧙', hat_santa: '🎅', hat_antlers: '🦌', hat_heartcrown: '💖',
  hat_bunny: '🐰', hat_petals: '🌼', hat_palm: '🌴',
  outfit_scarf: '🧣', outfit_cape: '🦸', outfit_bow: '🎀', outfit_medal: '🏅',
  outfit_raincoat: '🧥', outfit_umbrella: '☂️', outfit_sunglasses: '🕶️',
  outfit_puffer: '🧥', outfit_tshirt: '👕',
  outfit_ghost: '👻', outfit_sweater: '🎄', outfit_rose: '🌹', outfit_lovetux: '💘',
  outfit_butterfly: '🦋', outfit_floatie: '🛟', outfit_icecream: '🍦',
  bg_rainbow: '🌈', bg_stars: '⭐', bg_hearts: '💖', bg_nature: '🌿',
  bg_spooky: '🦇', bg_snowfall: '❄️', bg_cupid: '💘', bg_cherry: '🌸', bg_sunset: '🌅',
};

export default function OutfitPresets({ onApplied }) {
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchPresets = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/outfits`, { withCredentials: true });
      setPresets(data.presets || []);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPresets(); }, [fetchPresets]);

  const save = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error('Give your look a name ✨');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/outfits`, { name: trimmed }, { withCredentials: true });
      toast.success(`Saved look: ${trimmed}`);
      setName('');
      fetchPresets();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const apply = async (id, presetName) => {
    try {
      const { data } = await axios.post(`${API}/outfits/${id}/apply`, {}, { withCredentials: true });
      if (data.skipped && data.skipped.length > 0) {
        toast.info(`Applied "${presetName}" (${data.skipped.length} item(s) no longer owned)`);
      } else {
        toast.success(`Wearing: ${presetName} 💫`);
      }
      if (onApplied) await onApplied();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Apply failed');
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/outfits/${id}`, { withCredentials: true });
      toast.success('Outfit deleted');
      fetchPresets();
    } catch {
      toast.error('Delete failed');
    }
  };

  if (loading) {
    return <div className="neo-card p-4 text-center text-sm animate-pulse-soft">Loading outfits...</div>;
  }

  return (
    <div className="neo-card p-5 bg-[#FFFAF0] dark:bg-[#1f1f3a]" data-testid="outfit-presets">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-bold font-['Fredoka'] flex items-center gap-2">
          <Shirt className="w-5 h-5" /> My Looks
        </h3>
        <span className="text-xs opacity-70">{presets.length}/50</span>
      </div>

      {/* Save new look */}
      <div className="flex gap-2 mb-4">
        <input
          type="text"
          placeholder="Name your current look…"
          value={name}
          maxLength={40}
          onChange={(e) => setName(e.target.value)}
          className="flex-1 px-3 py-2 rounded-xl border-2 border-black text-sm bg-white dark:bg-[#2d2d54]"
          data-testid="outfit-name-input"
        />
        <button
          onClick={save}
          disabled={saving || !name.trim()}
          className="neo-btn px-3 py-2 bg-[#B9FBC0] text-sm font-bold flex items-center gap-1 disabled:opacity-50"
          data-testid="outfit-save-btn"
        >
          <Save className="w-4 h-4" /> Save
        </button>
      </div>

      {/* Presets list */}
      {presets.length === 0 ? (
        <p className="text-sm opacity-70 text-center py-6">
          No saved looks yet. Equip some accessories and save your first outfit! 💖
        </p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {presets.map((p) => {
            const emojis = Object.values(p.accessories || {}).map((id) => ITEM_EMOJI[id] || '✨');
            return (
              <div
                key={p.id}
                className="neo-card p-3 bg-white dark:bg-[#252545] flex items-center gap-3"
                data-testid={`outfit-preset-${p.id}`}
              >
                <div className="flex gap-0.5 text-2xl w-16 justify-center">
                  {emojis.slice(0, 3).map((e, i) => <span key={i}>{e}</span>)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold font-['Fredoka'] truncate">{p.name}</p>
                  <p className="text-xs opacity-60">{Object.keys(p.accessories || {}).length} item(s)</p>
                </div>
                <button
                  onClick={() => apply(p.id, p.name)}
                  className="neo-btn px-2 py-1 text-xs bg-[#A2D2FF] flex items-center gap-1"
                  data-testid={`outfit-apply-${p.id}`}
                  title="Wear this look"
                >
                  <Sparkles className="w-3 h-3" /> Wear
                </button>
                <button
                  onClick={() => remove(p.id)}
                  className="neo-btn p-1.5 bg-[#FF6B6B] text-white"
                  data-testid={`outfit-delete-${p.id}`}
                  title="Delete"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
